import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  generalInfoQuery,
  consolidatedInsightsQuery,
  principlesScoresItemInsightsQuery,
} from '../api';

import './styles.css';

import { navigation } from '../data';
import SubPagesLayout from '../../../UI/layout/subPages';
import PopupMessages from '../../../UI/introGameLayout/popupMessages';
import Analysis from '../../../UI/game/analysis';
import InstructorTransactionsModal from '../../../UI/instructor/decisionInsights/transactionsModal';

import {
  popupMessagesDecisionInsightsPrinciples,
  popupMessagesDecisionInsightsNoGamesData,
  decisionBreakdownElementsHeadersPrinciples
} from './data';

// Same Assumption / Principle / Constraint groupings as the student
// summary page (see ThePrinciples/data.js summaryApcCategories) — kept
// in sync with the instructor view so both surfaces label items
// identically. `items` must match `item_name` from the
// /api/principles/scores/insights/ response.
const APC_CATEGORIES = [
  { key: 'Assumptions', items: ['Economic Entity', 'Monetary Unit', 'Periodicity', 'Going Concern'] },
  { key: 'Principles',  items: ['Revenue Recognition', 'Expense Recognition (Matching)', 'Historical Cost', 'Full Disclosure'] },
  { key: 'Constraints', items: ['Cost-Benefit', 'Materiality', 'Conservatism'] },
];

const BAR_COLOR = '#B4E6EA';

function average(values) {
  const nums = values.filter(v => v != null && !Number.isNaN(v));
  if (!nums.length) return null;
  return nums.reduce((sum, v) => sum + v, 0) / nums.length;
}

export default function InstructorInsightsPrinciples() {
  const navigate = useNavigate();

  const [isModalShown, setIsModalShown] = useState(false);
  const [isDetailsButtonLoading, setIsDetailsButtonLoading] = useState(false);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);

  const [popupMessages, setPopupMessages] = useState([]);
  const [detailsButtonText, setDetailsButtonText] = useState(null);

  const { data: generalInfo } = useQuery(generalInfoQuery()) || {};
  const { data: consolidatedInsights } = useQuery({
    ...consolidatedInsightsQuery(),
    enabled: shouldFetchDetails,
  });
  const { data: principlesItemInsights } = useQuery(principlesScoresItemInsightsQuery());

  const games = generalInfo?.games;

  // Walk every record returned by /api/principles/scores/insights/ and
  // accumulate accuracy values per APC item_name. Each record is one
  // (game, session, attempt) row, so this gives us the cross-game,
  // cross-session average accuracy that the chart visualizes.
  const accuracyByName = useMemo(() => {
    const buckets = new Map();
    if (!Array.isArray(principlesItemInsights)) return buckets;
    principlesItemInsights.forEach(record => {
      (record?.accuracy?.items || []).forEach(item => {
        if (!item?.item_name) return;
        if (item.accuracy == null || Number.isNaN(item.accuracy)) return;
        if (!buckets.has(item.item_name)) buckets.set(item.item_name, []);
        buckets.get(item.item_name).push(item.accuracy);
      });
    });
    const averages = new Map();
    buckets.forEach((vals, name) => averages.set(name, average(vals)));
    return averages;
  }, [principlesItemInsights]);

  // Section 1 — three top-level category bars (Assumptions / Principles
  // / Constraints), each averaged from its child items. Rendered with
  // notApplicable when no records yet exist for any item under the
  // category so the chart shows "N/A" instead of a stark 0% bar.
  const categoryBars = useMemo(
    () => APC_CATEGORIES.map(c => {
      const accuracies = c.items
        .map(name => accuracyByName.get(name))
        .filter(v => v != null);
      if (!accuracies.length) {
        return { title: c.key, score: 0, color: BAR_COLOR, notApplicable: true };
      }
      const avg = average(accuracies);
      return { title: c.key, score: avg != null ? avg * 100 : 0, color: BAR_COLOR };
    }),
    [accuracyByName]
  );

  // Section 2 — every individual item with at least one observation,
  // ordered by APC_CATEGORIES so related concepts cluster together.
  const itemBars = useMemo(
    () => APC_CATEGORIES.flatMap(c =>
      c.items
        .map(name => ({ name, accuracy: accuracyByName.get(name) }))
        .filter(({ accuracy }) => accuracy != null)
        .map(({ name, accuracy }) => ({
          title: name,
          score: accuracy * 100,
          color: BAR_COLOR,
        }))
    ),
    [accuracyByName]
  );

  const data = useMemo(() => ([
    { bars: categoryBars, barsLabel: '%' },
    { title: 'Assumptions, Principles, and Constraints', bars: itemBars, barsLabel: '%' },
  ]), [categoryBars, itemBars]);

  const maxScore = useMemo(
    () => Math.max(100, ...data.flatMap(section => section.bars?.map(b => b.score) || [])),
    [data]
  );

  const hasChartData = principlesItemInsights?.length > 0;

  // Gate the popup off the same scores stream that paints the chart —
  // both the popup copy and the chart rows now derive from
  // /api/principles/scores/insights/ so they can never disagree.
  useEffect(() => {
    if (hasChartData) {
      setPopupMessages(popupMessagesDecisionInsightsPrinciples);
      setDetailsButtonText('View Details');
    } else if (principlesItemInsights) {
      setPopupMessages(popupMessagesDecisionInsightsNoGamesData);
    }
  }, [principlesItemInsights, hasChartData]);

  // View Details modal — unchanged from the previous implementation.
  // Builds the per-question breakdown table from consolidatedInsights
  // and pops the modal once that data lands.
  useEffect(() => {
    if (consolidatedInsights && shouldFetchDetails) {
      const principlesInsights = consolidatedInsights.principles || [];
      const header = 'Impact on Profits';

      const next = [];
      principlesInsights.forEach(insight => {
        const row = {
          question: insight.question,
          [header]: []
        };

        (insight.options || []).forEach(option => {
          const isCorrect = option.is_correct;

          let value;
          let backgroundColor = isCorrect ? '#D1E9D6' : '#FAEDED';

          if (!isCorrect && option.count_users >= 5) {
            value = `<span class="white">${option.option}<span class="decision-insights-count">${option.count_users}</span></span>`;
            backgroundColor = '#FD9595';
          } else {
            value = option.option;
          }

          row[header].push({
            value,
            isCorrect,
            background: backgroundColor,
          });
        });

        next.push(row);
      });

      setTransactionsData(next);
      setIsModalShown(true);
      setIsDetailsButtonLoading(false);
    }
  }, [consolidatedInsights, shouldFetchDetails]);

  const handlePrevButtonClick = () => {
    let redirectUrl = '/instructor/insights/decision-insights/overall';
    let navigateState = undefined;
    if(games && games.length > 0) {
      // Walk back through the inserted classified-multistep page
      // before falling further back to classifying-normal/overall.
      // Pass direction so multistep can keep travelling back if it
      // has nothing to show.
      if(games.some(game => game.game_type === 'Balanced Equation')) {
        redirectUrl = '/instructor/insights/decision-insights/double-entry';
      } else if(games.some(game => game.game_type === 'Account Classification')) {
        redirectUrl = '/instructor/insights/decision-insights/classified-multistep';
        navigateState = { direction: 'back' };
      } else if(games.some(game => game.game_type === 'Normal Balance')) {
        redirectUrl = '/instructor/insights/decision-insights/classifying-normal';
      }
    }
    navigate(redirectUrl, navigateState ? { state: navigateState } : undefined);
  };

  const handleNextButtonClick = () => {
    navigate('/instructor/insights/progress-scores/progress');
  };

  const handleDetailsButtonClick = () => {
    setIsDetailsButtonLoading(true);
    setShouldFetchDetails(true);
  };

  const handleModalCloseButtonClick = () => {
    setIsModalShown(false);
  };

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={5}
      pagesQuantity={5}
      contentClassName="decision-insights-content-container instructor-decision-insights-content-container"
    >
      <h1>INSIGHTS | OVERALL <b>| PRINCIPLES</b></h1>
      <div className="decision-insights-content">
        <PopupMessages
          messages={popupMessages}
          messageSize="xx-small"
          buttonText="Next"
          onButtonClick={handleNextButtonClick}
          additionalButtonText="Back"
          onAdditionalButtonClick={handlePrevButtonClick}
          isAvatarOnTop={true}
        />
        {hasChartData && (
          <div className="instructor-principles-analysis">
            <div className="instructor-principles-analysis-card">
              <Analysis
                heading="Accuracy Breakdown"
                data={data}
                size="small"
                maxScore={maxScore}
              />
              {detailsButtonText && (
                <button
                  type="button"
                  className={`scores-details-button ${isDetailsButtonLoading ? 'loading' : ''}`}
                  onClick={handleDetailsButtonClick}
                >
                  {!isDetailsButtonLoading && detailsButtonText}
                </button>
              )}
            </div>
          </div>
        )}
        {isModalShown && (
          <InstructorTransactionsModal
            onCloseButtonClick={handleModalCloseButtonClick}
            tableHeaders={decisionBreakdownElementsHeadersPrinciples}
            transactionsData={transactionsData}
            tableColumnHeader="Business Activity"
            hideColumnHeaders
            modalClassName="principles-decision-insights-modal"
          />
        )}
      </div>
    </SubPagesLayout>
  );
}
